// The single source of truth for what toasts exist, how long they live, and
// which one gives way when too many arrive. It knows nothing about React or
// sonner: `toast.tsx` renders whatever this holds.
//
// Rules come from the Open Design toast spec (mobile-app-toast.html, sections
// 「動畫與停留時間」「同時來兩則」「四個平台」). The web column differs from the phone
// columns in one respect: it stacks up to three toasts instead of showing one.

export type ToastTone = 'ok' | 'info' | 'err'

export interface ToastAction {
  /** Two-character verb such as 復原 / 重試 (English: Undo / Retry). */
  label: string
  onClick: () => void
}

export interface ToastInput {
  tone?: ToastTone
  title: string
  detail?: string
  action?: ToastAction
  /** Milliseconds. An action toast never lives shorter than 6s. */
  duration?: number
  /** Showing an existing id updates that toast in place and restarts its timer. */
  id?: string
}

export type ToastFn = (toast: ToastInput) => string

/** Internal: a toast that waits for something else to resolve it (`toast.loading`). */
export interface ToastShowInput extends ToastInput {
  loading?: boolean
}

export interface ToastEntry {
  readonly id: string
  /** Bumped whenever something the user can see changes, so the renderer re-measures. */
  readonly revision: number
  readonly tone: ToastTone
  readonly title: string
  readonly detail: string | undefined
  readonly action: ToastAction | undefined
  readonly loading: boolean
}

export type ToastHoldSource = 'pointer' | 'focus' | 'press'

export const MAX_VISIBLE_TOASTS = 3

const ONE_LINE_MS = 4000
const TWO_LINE_MS = 5000
const ACTION_MS = 6000
/** After a hold ends, never leave less than this, so a release does not close it at once. */
const RESUME_FLOOR_MS = 2000
/** One line holds 19 Chinese characters or 38 Latin ones (Chinese counts double). */
const ONE_LINE_WIDTH_UNITS = 38

const WIDE_CHARACTER = /[⺀-鿿豈-﫿＀-￯]/

function widthUnits(text: string): number {
  let units = 0
  for (const character of text) units += WIDE_CHARACTER.test(character) ? 2 : 1
  return units
}

/** How long a toast stays up; `null` means until something resolves it. */
export function toastDurationMs(
  input: Pick<ToastShowInput, 'title' | 'detail' | 'action' | 'duration' | 'loading'>
): number | null {
  if (input.loading) return null
  if (input.action) return Math.max(input.duration ?? ACTION_MS, ACTION_MS)
  if (input.duration !== undefined) return input.duration
  const twoLines = Boolean(input.detail) || widthUnits(input.title) > ONE_LINE_WIDTH_UNITS
  return twoLines ? TWO_LINE_MS : ONE_LINE_MS
}

interface LiveToast {
  entry: ToastEntry
  /** Milliseconds still to run; `null` for a toast with no timer. */
  remaining: number | null
  startedAt: number
  timer: ReturnType<typeof setTimeout> | null
  holds: Set<ToastHoldSource>
}

interface QueuedToast {
  id: string
  input: ToastShowInput
}

/** Toasts that must not be pushed out by a plain one: they carry an action, or are still working. */
const isProtected = (entry: Pick<ToastEntry, 'action' | 'loading'>) =>
  Boolean(entry.action) || entry.loading

/**
 * The same sentence again only restarts the timer when neither copy carries an
 * action: merging would replace the first action's callback, and with it the
 * first batch's undo.
 */
const sameSentence = (entry: ToastEntry, input: ToastShowInput): boolean =>
  !entry.action &&
  !input.action &&
  entry.tone === (input.tone ?? 'ok') &&
  entry.title === input.title &&
  entry.detail === input.detail &&
  !entry.loading &&
  !input.loading

export class ToastController {
  private live: LiveToast[] = []
  private queued: QueuedToast | null = null
  private pageHidden = false
  private snapshot: readonly ToastEntry[] = []
  private sequence = 0
  private readonly listeners = new Set<() => void>()

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getVisible = (): readonly ToastEntry[] => this.snapshot

  get = (id: string): ToastEntry | undefined =>
    this.live.find((item) => item.entry.id === id)?.entry

  show = (input: ToastShowInput): string => {
    if (input.id !== undefined) {
      const existing = this.live.find((item) => item.entry.id === input.id)
      if (existing) {
        this.update(existing, input)
        return existing.entry.id
      }
      if (this.queued?.id === input.id) {
        this.queued = { id: input.id, input }
        return input.id
      }
    } else {
      const twin = this.live.find((item) => sameSentence(item.entry, input))
      if (twin) {
        this.update(twin, input)
        return twin.entry.id
      }
    }

    const id = input.id ?? `toast-${++this.sequence}`
    const incomingProtected = Boolean(input.action) || Boolean(input.loading)

    if (this.live.length < MAX_VISIBLE_TOASTS) {
      this.add(id, input)
    } else {
      const evictable = this.live.findIndex((item) => !isProtected(item.entry))
      if (evictable >= 0) {
        this.removeAt(evictable, false)
        this.add(id, input)
      } else if (incomingProtected) {
        this.removeAt(0, false)
        this.add(id, input)
      } else {
        this.queued = { id, input }
      }
    }
    this.publish()
    return id
  }

  dismiss = (id?: string): void => {
    if (id === undefined) {
      for (const item of this.live) this.stop(item)
      this.live = []
      this.queued = null
      this.publish()
      return
    }
    if (this.queued?.id === id) {
      this.queued = null
      return
    }
    const index = this.live.findIndex((item) => item.entry.id === id)
    if (index < 0) return
    this.removeAt(index, true)
    this.publish()
  }

  /** Escape: close the toast that arrived last. */
  dismissNewest = (): void => {
    const newest = this.live.at(-1)
    if (newest) this.dismiss(newest.entry.id)
  }

  /** Hover, keyboard focus and pointer-down pause the timer; release tops it up to 2s. */
  hold = (id: string, source: ToastHoldSource, held: boolean): void => {
    const item = this.live.find((candidate) => candidate.entry.id === id)
    if (!item) return
    if (held) {
      item.holds.add(source)
      this.pause(item)
      return
    }
    item.holds.delete(source)
    if (item.holds.size === 0) this.resume(item)
  }

  /** The tab or app is in the background: nothing counts down. */
  setPageHidden = (hidden: boolean): void => {
    if (this.pageHidden === hidden) return
    this.pageHidden = hidden
    for (const item of this.live) {
      if (hidden) this.pause(item)
      else if (item.holds.size === 0) this.resume(item)
    }
  }

  /** Drop everything. Tests use it between cases. */
  reset = (): void => {
    for (const item of this.live) this.stop(item)
    this.live = []
    this.queued = null
    this.pageHidden = false
    this.publish()
  }

  private add(id: string, input: ToastShowInput): void {
    const duration = toastDurationMs(input)
    const item: LiveToast = {
      entry: this.entryFor(id, input, 1),
      remaining: duration,
      startedAt: 0,
      timer: null,
      holds: new Set(),
    }
    this.live.push(item)
    this.arm(item)
  }

  private update(item: LiveToast, input: ToastShowInput): void {
    const next = this.entryFor(item.entry.id, input, item.entry.revision)
    const changed =
      next.tone !== item.entry.tone ||
      next.title !== item.entry.title ||
      next.detail !== item.entry.detail ||
      next.loading !== item.entry.loading ||
      next.action?.label !== item.entry.action?.label
    item.entry = changed ? { ...next, revision: item.entry.revision + 1 } : next
    this.stop(item)
    item.remaining = toastDurationMs(input)
    if (item.holds.size === 0 && !this.pageHidden) this.arm(item)
    this.publish()
  }

  private entryFor(id: string, input: ToastShowInput, revision: number): ToastEntry {
    return {
      id,
      revision,
      tone: input.tone ?? 'ok',
      title: input.title,
      detail: input.detail,
      action: input.action,
      loading: Boolean(input.loading),
    }
  }

  private removeAt(index: number, promote: boolean): void {
    const [removed] = this.live.splice(index, 1)
    if (removed) this.stop(removed)
    if (promote && this.queued && this.live.length < MAX_VISIBLE_TOASTS) {
      const { id, input } = this.queued
      this.queued = null
      this.add(id, input)
    }
  }

  private arm(item: LiveToast): void {
    if (item.remaining === null || item.holds.size > 0 || this.pageHidden) return
    item.startedAt = Date.now()
    item.timer = setTimeout(() => this.dismiss(item.entry.id), item.remaining)
  }

  private stop(item: LiveToast): void {
    if (item.timer !== null) clearTimeout(item.timer)
    item.timer = null
  }

  private pause(item: LiveToast): void {
    if (item.timer === null || item.remaining === null) return
    clearTimeout(item.timer)
    item.timer = null
    item.remaining = Math.max(0, item.remaining - (Date.now() - item.startedAt))
  }

  private resume(item: LiveToast): void {
    if (item.remaining === null || item.timer !== null || this.pageHidden) return
    item.remaining = Math.max(item.remaining, RESUME_FLOOR_MS)
    this.arm(item)
  }

  private publish(): void {
    this.snapshot = this.live.map((item) => item.entry)
    for (const listener of [...this.listeners]) listener()
  }
}

export const toastController = new ToastController()
